import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ArticleSummary } from '@/lib/content';
import { fetchArticles } from '@/lib/content';
import { formatDate } from '@/lib/format';
import { Card } from '@/components/Card';
import { Icon } from '@/components/Icon';
import { Reveal } from '@/components/Reveal';
import { Section, SectionHeading } from '@/components/Section';
import { Seo } from '@/components/Seo';

/** 技术文章列表。内容由运营后台维护，草稿不会出现在这里。 */
export function ArticlesPage() {
  const [items, setItems] = useState<ArticleSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetchArticles(controller.signal)
      .then(setItems)
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setError(caught instanceof Error ? caught.message : '文章加载失败');
      });
    return () => controller.abort();
  }, []);

  return (
    <>
      <Seo
        path="/articles"
        title="技术文章 — 工程实现与技术取舍 | Havorix™"
        description="Havorix™ 的技术文章：协议与链路实现、前端取舍、权限与审计等工程细节，由运营后台持续更新。"
        crumbs={[
          { name: '首页', path: '/' },
          { name: '技术文章', path: '/articles' },
        ]}
      />

      <Section tone="grid" spacing="tight" className="border-b border-line">
        <div className="max-w-3xl">
          <span className="label-mono">技术文章</span>
          <h1 className="mt-3 text-3xl font-semibold leading-tight text-fg md:text-[2.5rem] md:leading-[1.15]">
            工程实现与技术取舍
          </h1>
          <p className="mt-4 text-base leading-relaxed text-fg-muted">
            记录平台在协议、链路、权限与前端实现上的具体选择。文章由运营后台维护，发布后即时可见。
          </p>
        </div>
      </Section>

      <Section>
        <SectionHeading eyebrow={items ? `共 ${items.length} 篇` : '加载中'} title="全部技术文章" />

        {error ? (
          <div className="mt-8 rounded-card border border-line bg-surface-2 p-5 text-sm text-fg-muted">
            {error}
          </div>
        ) : items === null ? (
          <p className="mt-8 text-sm text-fg-muted">正在加载技术文章…</p>
        ) : items.length === 0 ? (
          <div className="mt-8 rounded-card border border-line bg-surface-2 p-8 text-center">
            <p className="text-sm text-fg-muted">暂时还没有发布的技术文章。</p>
            <Link to="/news" className="mt-3 inline-flex items-center gap-1.5 text-sm text-brand">
              先看看工程动态
              <Icon name="arrowRight" size={14} />
            </Link>
          </div>
        ) : (
          <div className="mt-10 flex flex-col gap-4">
            {items.map((item, index) => (
              <Reveal key={item.id} delay={index} as="div">
                <Card interactive className="group">
                  <article className="flex flex-col gap-3">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-fg-subtle">
                      <span className="metric-value">
                        {item.publishedAt ? formatDate(item.publishedAt) : '未注明日期'}
                      </span>
                      <span aria-hidden="true">·</span>
                      <span>约 {item.minutes} 分钟</span>
                    </div>

                    <h2 className="text-lg font-semibold text-fg">
                      <Link to={`/articles/${item.slug}`} className="transition-colors hover:text-accent">
                        {item.title}
                      </Link>
                    </h2>

                    <p className="text-sm leading-relaxed text-fg-muted">{item.excerpt}</p>

                    <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
                      <div className="flex flex-wrap gap-2">
                        {item.tags.map((tag) => (
                          <span
                            key={tag}
                            className="rounded-md bg-grad-tint px-2.5 py-1 text-xs font-medium text-brand"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <Link
                        to={`/articles/${item.slug}`}
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-fg transition-colors hover:text-accent"
                      >
                        阅读
                        <Icon name="arrowRight" size={14} />
                      </Link>
                    </div>
                  </article>
                </Card>
              </Reveal>
            ))}
          </div>
        )}
      </Section>
    </>
  );
}
